import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Plus, Circle, ListBullets, Rows, MagnifyingGlass, Trash, Wrench, XCircle, ArrowClockwise, ClockCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Inventory() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [tires, setTires] = useState([]);
  const [lifetimeKm, setLifetimeKm] = useState({});
  const [mountCount, setMountCount] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [addedThisSession, setAddedThisSession] = useState(0);
  const [serial, setSerial] = useState('');
  const [bulkSerials, setBulkSerials] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [shared, setShared] = useState({
    size: '', type: '', tread_depth: '',
    purchase_date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);
  const [filterTab, setFilterTab] = useState('all');
  const serialInputRef = useRef(null);

  useEffect(() => { if (profile) fetchInventory(); }, [profile]);

  const fetchInventory = async () => {
    try {
      const { data: tireRows, error: tiresError } = await supabase
        .from('tires').select('*').eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });
      if (tiresError) throw tiresError;
      setTires(tireRows || []);

      const { data: posRows, error: posError } = await supabase
        .from('tire_positions').select('tire_id, mounted_km, dismounted_km, tires!inner(company_id)')
        .eq('tires.company_id', profile.company_id);
      if (posError) throw posError;

      const km = {}; const mounts = {};
      (posRows || []).forEach(p => {
        if (!p.tire_id) return;
        mounts[p.tire_id] = (mounts[p.tire_id] || 0) + 1;
        if (p.mounted_km != null && p.dismounted_km != null) {
          km[p.tire_id] = (km[p.tire_id] || 0) + (p.dismounted_km - p.mounted_km);
        }
      });
      setLifetimeKm(km); setMountCount(mounts);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Gagal memuat stok ban');
    }
  };

  const addSingleTire = async (serialNum) => {
    const { error } = await supabase.from('tires').insert([{
      company_id: profile.company_id, serial_number: serialNum.trim(),
      type: shared.type, size: shared.size,
      tread_depth: parseFloat(shared.tread_depth),
      purchase_date: shared.purchase_date, status: 'stock',
    }]);
    return error;
  };

  const validateShared = () => {
    if (!shared.size.trim() || !shared.type.trim() || !shared.tread_depth) {
      toast.error('Lengkapi Ukuran, Tipe, dan Kedalaman Tapak');
      return false;
    }
    return true;
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!serial.trim()) return;
    if (!validateShared()) return;
    setLoading(true);
    const error = await addSingleTire(serial);
    setLoading(false);
    if (error) { toast.error(`Gagal: ${error.message}`); return; }
    toast.success(`Ban ${serial.trim()} ditambahkan`);
    setSerial(''); setAddedThisSession((c) => c + 1);
    fetchInventory();
    setTimeout(() => serialInputRef.current?.focus(), 50);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!validateShared()) return;
    const lines = bulkSerials.split('\n').map(s => s.trim()).filter(Boolean);
    if (lines.length === 0) { toast.error('Masukkan minimal satu nomor seri'); return; }
    setLoading(true);
    let success = 0; let failed = 0; const failedSerials = [];
    for (const sn of lines) {
      const error = await addSingleTire(sn);
      if (error) { failed++; failedSerials.push(sn); } else { success++; }
    }
    setLoading(false);
    setAddedThisSession((c) => c + success);
    if (success > 0) toast.success(`${success} ban ditambahkan`);
    if (failed > 0) toast.error(`${failed} gagal: ${failedSerials.slice(0,3).join(', ')}${failedSerials.length > 3 ? '...' : ''}`);
    setBulkSerials(''); fetchInventory();
  };

  const closeForm = () => {
    setShowAddForm(false); setBulkMode(false);
    setSerial(''); setBulkSerials(''); setAddedThisSession(0);
  };

  const STATUS_LABELS = {
    stock: 'Bekas/Siap',
    repair: 'Perbaikan',
    scrap: 'Scrap',
  };

  const changeStatus = async (tire, newStatus) => {
    const label = STATUS_LABELS[newStatus] || newStatus;
    if (newStatus === 'scrap') {
      if (!window.confirm(`Vonis ban ${tire.serial_number} sebagai SCRAP?\n\nStatus scrap bersifat final dan TIDAK BISA diubah lagi.`)) return;
    } else {
      if (!window.confirm(`Ubah status ban ${tire.serial_number} menjadi ${label}?`)) return;
    }
    try {
      const { error } = await supabase.from('tires').update({ status: newStatus }).eq('id', tire.id);
      if (error) throw error;

      // If marking as scrap from inventory (no fresh dismount), insert a minimal scrap record
      // so the tire appears in scrap reports with at least cumulative lifetime KM captured.
      if (newStatus === 'scrap') {
        const existing = await supabase.from('scrap_records').select('id').eq('tire_id', tire.id).maybeSingle();
        if (!existing.data) {
          await supabase.from('scrap_records').insert([{
            tire_id: tire.id,
            damage_types: ['Tidak dijelaskan'],
            tread_depth: tire.tread_depth,
            km_total: lifetimeKm[tire.id] || 0,
            notes: 'Divonis scrap manual dari menu Stok Ban',
            scrapped_at: new Date().toISOString(),
          }]);
        }
      }
      toast.success(`Ban ${tire.serial_number} → ${label}`);
      fetchInventory();
    } catch (error) {
      console.error('Error updating tire status:', error);
      toast.error(error.message || 'Gagal memperbarui status');
    }
  };

  const deleteTire = async (tire) => {
    if (!window.confirm(`Hapus ban ${tire.serial_number}? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      const { error } = await supabase.from('tires').delete().eq('id', tire.id);
      if (error) throw error;
      toast.success(`Ban ${tire.serial_number} dihapus`);
      fetchInventory();
    } catch (error) {
      console.error('Error deleting tire:', error);
      toast.error('Gagal menghapus ban');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'stock': return 'text-zinc-700';
      case 'mounted': return 'text-zinc-900';
      case 'repair': return 'text-amber-600';
      case 'scrap': return 'text-red-600';
      default: return 'text-zinc-700';
    }
  };

  const getStatusLabel = (tire) => {
    const cycles = mountCount[tire.id] || 0;
    if (tire.status === 'stock') return cycles > 0 ? 'Stok Bekas' : 'Stok Baru';
    if (tire.status === 'mounted') return 'Terpasang';
    if (tire.status === 'repair') return 'Diperbaiki';
    if (tire.status === 'scrap') return 'Rusak';
    return tire.status;
  };

  const tabFilter = (tire) => {
    const cycles = mountCount[tire.id] || 0;
    switch (filterTab) {
      case 'all': return true;
      case 'new': return tire.status === 'stock' && cycles === 0;
      case 'used': return tire.status === 'stock' && cycles > 0;
      case 'mounted': return tire.status === 'mounted';
      case 'repair': return tire.status === 'repair';
      case 'scrap': return tire.status === 'scrap';
      default: return true;
    }
  };

  const searchFilter = (tire) =>
    !searchTerm.trim() || tire.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());

  const filteredTires = tires.filter(t => tabFilter(t) && searchFilter(t));

  // Group by size
  const grouped = filteredTires.reduce((acc, t) => {
    const key = t.size || 'Tanpa Ukuran';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const sizeOrder = Object.keys(grouped).sort();

  const tabs = [
    { value: 'all', label: 'Semua' },
    { value: 'new', label: 'Stok Baru' },
    { value: 'used', label: 'Stok Bekas' },
    { value: 'mounted', label: 'Terpasang' },
    { value: 'repair', label: 'Diperbaiki' },
    { value: 'scrap', label: 'Rusak' },
  ];

  const counts = {
    all: tires.length,
    new: tires.filter(t => t.status === 'stock' && (mountCount[t.id] || 0) === 0).length,
    used: tires.filter(t => t.status === 'stock' && (mountCount[t.id] || 0) > 0).length,
    mounted: tires.filter(t => t.status === 'mounted').length,
    repair: tires.filter(t => t.status === 'repair').length,
    scrap: tires.filter(t => t.status === 'scrap').length,
  };

  const renderRow = (tire) => {
    const km = lifetimeKm[tire.id] || 0;
    const cycles = mountCount[tire.id] || 0;
    const canDelete = cycles === 0 && tire.status === 'stock';
    const isUsedStock = tire.status === 'stock' && cycles > 0;
    const isRepair = tire.status === 'repair';
    return (
      <tr key={tire.id} className="border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50" data-testid={`tire-row-${tire.serial_number}`}>
        <td className="p-4">
          <button
            onClick={() => navigate(`/history?serial=${encodeURIComponent(tire.serial_number)}`)}
            className="text-zinc-900 font-mono text-sm hover:text-red-600 hover:underline underline-offset-2 inline-flex items-center gap-1.5 transition-colors"
            title="Lihat riwayat lengkap"
            data-testid={`view-history-${tire.serial_number}`}
          >
            <ClockCounterClockwise size={13} weight="bold" className="text-zinc-400" />
            {tire.serial_number}
          </button>
        </td>
        <td className="p-4 text-zinc-700 font-mono text-sm">{tire.type}</td>
        <td className="p-4 text-zinc-700 font-mono text-sm">{tire.tread_depth}</td>
        <td className="p-4">
          <span className={`flex items-center gap-2 font-mono text-sm uppercase ${getStatusColor(tire.status)}`}>
            <Circle size={8} weight="fill" />
            {getStatusLabel(tire)}
          </span>
        </td>
        <td className="p-4 text-zinc-900 font-mono text-sm">{km.toLocaleString('id-ID')}</td>
        <td className="p-4 text-zinc-700 font-mono text-sm">{cycles}</td>
        <td className="p-4 text-zinc-700 font-mono text-sm">{new Date(tire.purchase_date).toLocaleDateString('id-ID')}</td>
        <td className="p-4">
          <div className="flex gap-2 flex-wrap">
            {isUsedStock && (
              <>
                <button onClick={() => changeStatus(tire, 'repair')}
                  className="flex items-center gap-1 border border-amber-300 hover:bg-amber-50 text-amber-700 font-mono uppercase text-[10px] tracking-wider py-2 px-3 rounded-lg transition-colors"
                  data-testid={`to-repair-${tire.serial_number}`}>
                  <Wrench size={12} weight="bold" />
                  PERBAIKAN
                </button>
                <button onClick={() => changeStatus(tire, 'scrap')}
                  className="flex items-center gap-1 border border-red-300 hover:bg-red-50 text-red-600 font-mono uppercase text-[10px] tracking-wider py-2 px-3 rounded-lg transition-colors"
                  data-testid={`to-scrap-${tire.serial_number}`}>
                  <XCircle size={12} weight="bold" />
                  SCRAP
                </button>
              </>
            )}
            {isRepair && (
              <>
                <button onClick={() => changeStatus(tire, 'stock')}
                  className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-[10px] tracking-wider py-2 px-3 rounded-lg shadow-sm transition-colors"
                  data-testid={`to-ready-${tire.serial_number}`}>
                  <ArrowClockwise size={12} weight="bold" />
                  KEMBALI SIAP
                </button>
                <button onClick={() => changeStatus(tire, 'scrap')}
                  className="flex items-center gap-1 border border-red-300 hover:bg-red-50 text-red-600 font-mono uppercase text-[10px] tracking-wider py-2 px-3 rounded-lg transition-colors"
                  data-testid={`to-scrap-${tire.serial_number}`}>
                  <XCircle size={12} weight="bold" />
                  SCRAP
                </button>
              </>
            )}
            {canDelete && (
              <button onClick={() => deleteTire(tire)}
                className="flex items-center gap-1 border border-red-300 hover:bg-red-50 text-red-600 font-mono uppercase text-[10px] tracking-wider py-2 px-3 rounded-lg transition-colors"
                data-testid={`delete-${tire.serial_number}`}>
                <Trash size={12} weight="bold" />
                HAPUS
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="p-4 sm:p-8" data-testid="inventory-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-widest text-zinc-900" style={{ fontFamily: 'Chivo' }}>
          STOK BAN
        </h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 flex items-center gap-2 transition-colors rounded-lg shadow-sm"
          data-testid="add-tire-button"
        >
          <Plus size={16} weight="bold" />
          TAMBAH BAN
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8 shadow-sm" data-testid="add-tire-form">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 pb-2 mb-6">
            <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
              BAN BARU {addedThisSession > 0 && <span className="text-sm font-mono text-red-600 ml-3">{addedThisSession} ditambahkan sesi ini</span>}
            </h2>
            <div className="flex border border-zinc-300 rounded-lg overflow-hidden">
              <button onClick={() => setBulkMode(false)}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-2 ${!bulkMode ? 'bg-red-600 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-100'}`}
                data-testid="mode-single">
                <Rows size={14} weight="bold" />SATU
              </button>
              <button onClick={() => setBulkMode(true)}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-2 ${bulkMode ? 'bg-red-600 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-100'}`}
                data-testid="mode-bulk">
                <ListBullets size={14} weight="bold" />MASSAL
              </button>
            </div>
          </div>

          {/* Shared fields — order: Ukuran → Tipe → Tapak → Tanggal */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Ukuran *</label>
              <input type="text" value={shared.size} onChange={(e) => setShared({ ...shared, size: e.target.value })}
                placeholder="contoh: 12.00R24"
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                required data-testid="tire-size-input" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Tipe *</label>
              <input type="text" value={shared.type} onChange={(e) => setShared({ ...shared, type: e.target.value })}
                placeholder="contoh: CB972"
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                required data-testid="tire-type-input" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Kedalaman Tapak (mm) *</label>
              <input type="number" step="0.1" value={shared.tread_depth} onChange={(e) => setShared({ ...shared, tread_depth: e.target.value })}
                placeholder="contoh: 32"
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                required data-testid="tread-depth-input" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Tanggal Masuk *</label>
              <input type="date" value={shared.purchase_date} onChange={(e) => setShared({ ...shared, purchase_date: e.target.value })}
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                required data-testid="purchase-date-input" />
            </div>
          </div>

          {bulkMode ? (
            <form onSubmit={handleBulkSubmit}>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">
                Nomor Seri (satu per baris) * — tempel 10-30 nomor seri sekaligus
              </label>
              <textarea value={bulkSerials} onChange={(e) => setBulkSerials(e.target.value)}
                rows={10} placeholder={'SN-001\nSN-002\nSN-003\n...'}
                className="w-full bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                data-testid="bulk-serials-input" />
              <p className="text-xs font-mono text-zinc-500 mt-1">
                {bulkSerials.split('\n').map(s => s.trim()).filter(Boolean).length} nomor seri terdeteksi
              </p>
              <div className="flex gap-3 mt-4">
                <button type="submit" disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                  data-testid="submit-bulk-button">
                  {loading ? 'MENAMBAHKAN...' : 'TAMBAH SEMUA'}
                </button>
                <button type="button" onClick={closeForm}
                  className="border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg transition-colors"
                  data-testid="close-bulk-button">
                  SELESAI
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSingleSubmit}>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Nomor Seri *</label>
              <div className="flex flex-wrap gap-3">
                <input ref={serialInputRef} type="text" value={serial} onChange={(e) => setSerial(e.target.value)}
                  placeholder="Ketik nomor seri lalu tekan Enter"
                  className="flex-1 min-w-[200px] bg-white border border-zinc-300 rounded-lg p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                  required autoFocus data-testid="serial-number-input" />
                <button type="submit" disabled={loading || !serial.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="submit-tire-button">
                  {loading ? 'MENAMBAHKAN...' : 'TAMBAH & LANJUT'}
                </button>
                <button type="button" onClick={closeForm}
                  className="border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-6 rounded-lg transition-colors"
                  data-testid="cancel-tire-button">
                  SELESAI
                </button>
              </div>
              <p className="text-xs font-mono text-zinc-500 mt-2">Tip: Form tetap terbuka setelah menambah. Ketik nomor seri lalu Enter untuk ban berikutnya tanpa muat ulang halaman.</p>
            </form>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Cari berdasarkan nomor seri..."
          className="w-full bg-white border border-zinc-300 rounded-lg p-3 pl-10 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
          data-testid="inventory-search-input"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="status-filter">
        {tabs.map((tab) => (
          <button key={tab.value} onClick={() => setFilterTab(tab.value)}
            className={`px-4 py-2 font-mono uppercase text-xs tracking-wider transition-colors whitespace-nowrap flex items-center gap-2 rounded-lg ${
              filterTab === tab.value ? 'bg-red-600 text-white shadow-sm' : 'border border-zinc-300 text-zinc-700 hover:bg-zinc-100'
            }`}
            data-testid={`filter-${tab.value}`}>
            {tab.label}
            <span className={`${filterTab === tab.value ? 'text-white/80' : 'text-zinc-400'} text-[10px]`}>
              {counts[tab.value]}
            </span>
          </button>
        ))}
      </div>

      {/* Grouped by size */}
      <div className="space-y-6" data-testid="tires-grouped">
        {sizeOrder.map((sz) => (
          <div key={sz} className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm" data-testid={`size-group-${sz}`}>
            <div className="flex items-center justify-between p-4 border-b border-zinc-200 bg-zinc-50">
              <h2 className="text-base font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
                UKURAN: {sz}
              </h2>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">
                {grouped[sz].length} ban
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/50">
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Nomor Seri</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tipe</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tapak (mm)</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Status</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Umur (KM)</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Siklus</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tanggal Masuk</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Aksi</th>
                  </tr>
                </thead>
                <tbody>{grouped[sz].map(renderRow)}</tbody>
              </table>
            </div>
          </div>
        ))}
        {sizeOrder.length === 0 && (
          <div className="text-center py-20 bg-white border border-zinc-200 rounded-xl">
            <p className="text-zinc-500 font-mono text-sm">
              {searchTerm ? 'Tidak ada ban yang cocok' : 'Tidak ada ban ditemukan'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
